'use client';

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

import { useNavigate } from 'react-router-dom';
import { useScrollDirection } from '@/hooks/useScrollDirection';
import { useGlobal } from '@/contexts/GlobalContext';
import { useSdHeaderMenuDrawer } from '@/contexts/SdHeaderMenuDrawerContext';
import { useNotificationContext } from '@/contexts/NotificationContext';
import SdHeaderMenuDrawer from '@/components/layouts/SdHeaderMenuDrawer';
import { Status } from '@/types/teams';
import Tab from '@/components/layouts/Tab';
import { useCurrentTabMenu } from '@/hooks/useCurrentTabMenu';
import { isTeamConfigInitialized } from '@/utils/teams';

/**
 * SDヘッダー
 */
export default function SdHeader() {
  const navigate = useNavigate();
  const isVisible = useScrollDirection();

  /**
   * React context
   */
  const context = useGlobal();
  const { menuItems, activeIndex } = useCurrentTabMenu();

  /**
   * React context
   */
  const { isOpen, openDrawer, closeDrawer } = useSdHeaderMenuDrawer();
  const { allConfirmed } = useNotificationContext();

  /**
   * useState
   */
  const [showBadge, setShowBadge] = useState<boolean>(false);

  /**
   * ドロワー表示（プロフィールカード表示）
   */
  const handleOpenDrawer = () => {
    openDrawer(<SdHeaderMenuDrawer />);
  };

  const handleMoveToNotification = () => {
    isOpen && closeDrawer();
    navigate('/notifications');
  };

  // チーム招待があるかどうか
  const hasTeamInvitation = (userTeams: typeof context.user_teams) => {
    return (userTeams || []).some((userTeam) => userTeam.status === Status.Requested);
  };

  // チームの初期設定が完了していないかを判定する関数(複数チームを持つ場合)
  const isNotTeamsConfigInitialized = (userTeams: typeof context.user_teams, userId: number | undefined): boolean => {
    return (userTeams ?? []).some(
      (userTeam) => userTeam.team.owner_id === userId && !isTeamConfigInitialized(userTeam.team)
    );
  };

  useEffect(() => {
    setShowBadge(
      hasTeamInvitation(context.user_teams) || isNotTeamsConfigInitialized(context.user_teams, context.user?.id)
    );
  }, [context.user_teams, context.user]);

  return (
    <header
      className={`
        transform transition-transform duration-300 ease-in-out
        ${isVisible ? 'translate-y-0' : '-translate-y-full'}
      `}>
      <div className=" flex justify-between items-center select-none px-2 py-2 h-10 bg-custom-gradient2">
        {/* ハンバーガーMENU */}
        {!isOpen ? (
          <button
            type="button"
            onClick={handleOpenDrawer}
            className="relative text-white bg-transparent hover:bg-gray-100 hover:bg-opacity-25 rounded-lg text-sm w-8 h-8 flex items-center justify-center">
            <svg
              className="w-4 h-4"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 17 14">
              <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M1 1h15M1 7h15M1 13h15"
              />
            </svg>
            <span className="sr-only">Open menu</span>
            {showBadge && (
              <div className="absolute top-1 right-0 w-3 h-3 rounded-full border border-white bg-blue-600"></div>
            )}
          </button>
        ) : (
          <button
            type="button"
            onClick={closeDrawer}
            className="text-white bg-transparent hover:bg-gray-100 hover:bg-opacity-25 rounded-lg text-sm w-8 h-8 flex items-center justify-center">
            <svg
              className="w-4 h-4"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 14 14">
              <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
              />
            </svg>
            <span className="sr-only">Close menu</span>
          </button>
        )}

        {/* ロゴ */}
        <Link to="/home">
          <img src="/footballlog.svg" alt="logo" width={128} height={26} />
        </Link>

        {/* 通知 */}
        <button
          type="button"
          onClick={handleMoveToNotification}
          className="relative text-white bg-transparent hover:bg-gray-100 hover:bg-opacity-25 rounded-lg text-sm w-8 h-8 flex items-center justify-center">
          <svg
            className="w-5 h-5"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 16 21">
            <path
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M8 3.464V1.1m0 2.365a5.338 5.338 0 0 1 5.133 5.368v1.8c0 2.386 1.867 2.982 1.867 4.175C15 15.4 15 16 14.462 16H1.538C1 16 1 15.4 1 14.807c0-1.193 1.867-1.789 1.867-4.175v-1.8A5.338 5.338 0 0 1 8 3.464ZM4.54 16a3.48 3.48 0 0 0 6.92 0H4.54Z"
            />
          </svg>
          {!allConfirmed && (
            <div className="absolute top-1 right-1 w-3 h-3 rounded-full border border-white bg-blue-600"></div>
          )}
        </button>
      </div>
      <Tab menuItems={menuItems} activeIndex={activeIndex} />
    </header>
  );
}
